/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Undo from '@material-ui/icons/Undo';
import DoubleUndo from 'components/icons/DoubleUndoIcon';
import Button from 'components/common/Button';
import { Box } from '@material-ui/core';
import SubmissionStatusChangeDialog from 'components/status/SubmissionStatusChangeDialog';
import { Theme as MaterialUITheme } from '@rjsf/material-ui';
import withTheme from '../../../components/submission/rjsf/withTheme';
import {
  arrayTemplates,
  fields,
  fieldTemplate,
  objectFieldTemplate,
  widgets,
} from '../../../components/submission/templates';
import usePaymentForm from './usePaymentForm';
import { handleBusinessRuleValidations, postProcess } from '../../../shared/configuration/businessRules';
import LoadingPreview from '../../../components/submission/LoadingPreview';
import WelcomeSection from '../../../components/welcomeSection/WelcomeSection';
import { SubmissionContextProvider } from '../../../components/submission/context/SubmissionContextProvider';
import { handleCancelError } from '../../../api/helpers/api.helpers';

const MuiForm = withTheme(MaterialUITheme);

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  heading: {
    marginBottom: '1rem',
    position: 'relative',
  },
  buttonBackToDraft: {
    marginRight: '1rem',
  },
  buttonWrapper: {
    right: 0,
    top: '10rem',
  },
  paper: {
    margin: '1.5rem 0',
    padding: '1rem 1rem',
    backgroundColor: theme.palette.elementBackground,
    boxShadow: 'none',
    borderRadius: '1rem',

    [theme.breakpoints.up('md')]: {
      padding: '2rem 3rem',
    },
    [theme.breakpoints.up('xl2')]: {
      width: '75%',
      maxWidth: '60rem',
    },
  },
}));

/**
 * Payment form that works with a sliced pair of jsonSchema and uiSchema containing the Payment section.
 * @returns {JSX.Element}
 * @constructor
 */
function PaymentForm({
  validationSchema,
  uiSchema,
  setUiSchema,
  submission,
  setSubmission,
  extraErrors,
  setExtraErrors,
  setWorkflowValidations,
  skipValidation,
}) {
  const idPrefix = 'paymentSection';
  const classes = useStyles();
  const { t } = useTranslation();
  const transformErrors = errs => errs;

  const onChange = async (props, signal, doPostProcess = false, event = null, propertyId = null) => {
    if (skipValidation) {
      return;
    }
    try {
      const { formData } = props;
      let newSubmission = formData;
      if (doPostProcess) {
        newSubmission = await postProcess(newSubmission, event, propertyId, signal);
      }
      const {
        workflowValidations: newWorkflowValidations,
        uiSchema: newUiSchema,
      } = await handleBusinessRuleValidations(newSubmission, uiSchema, null, null, signal);
      setWorkflowValidations(newWorkflowValidations);
      setExtraErrors(newWorkflowValidations.extraErrors);
      setUiSchema(newUiSchema);
      setSubmission(newSubmission);
    } catch (error) {
      handleCancelError(error);
    }
  };

  const onSectionChange = async (props, signal) => {
    const { formData, changeProps } = props;
    if (changeProps?.sectionName) {
      if (!changeProps?.sectionItemId) {
        setSubmission(formData);
        return;
      }
      const { sectionName, sectionOperation, sectionItemId } = changeProps;
      const event = `${sectionName}_${sectionOperation}`;
      await onChange(props, signal, true, event, sectionItemId);
    } else {
      const { propertyId } = changeProps;
      const event = `${propertyId}_change`;
      await onChange(props, signal, true, event, propertyId);
    }
  };

  const { dialogStatusChange, handleBackToDraftButton, handleBackToReadyToSignButton } = usePaymentForm(submission);

  return (
    <>
      <div className={classes.heading}>
        <WelcomeSection
          title={t('submission.payment.payment-of-fees')}
          description={t('submission.payment.header-description')}
          contrast="true"
        />
        <Box display="flex" justifyContent="flex-end" className={classes.buttonWrapper}>
          <Button
            startIcon={<DoubleUndo />}
            className={classes.buttonBackToDraft}
            color="default"
            type="body"
            onClick={handleBackToDraftButton}
          >
            {t('back.to.draft')}
          </Button>
          <Button startIcon={<Undo />} color="default" type="body" onClick={handleBackToReadyToSignButton}>
            {t('back.to.ready.to.sign')}
          </Button>
        </Box>
        <SubmissionStatusChangeDialog
          open={dialogStatusChange.isOpen}
          handleConfirm={dialogStatusChange.confirm}
          handleCancel={dialogStatusChange.cancel}
          submission={submission}
          revertStatusAction={dialogStatusChange.revertStatusAction}
        />
      </div>
      {!isEmpty(validationSchema) && !isEmpty(uiSchema) && !isEmpty(submission) ? (
        <SubmissionContextProvider>
          <MuiForm
            translation={t}
            className={classes.form}
            idPrefix={idPrefix}
            id={idPrefix}
            schema={validationSchema}
            uiSchema={uiSchema}
            ArrayFieldTemplate={arrayTemplates.formEntitiesArrayTemplate}
            ObjectFieldTemplate={objectFieldTemplate}
            FieldTemplate={fieldTemplate}
            fields={fields}
            widgets={widgets}
            formData={submission}
            formContext={{
              classes,
              submissionId: submission.id,
              attachments: submission?.attachments,
            }}
            onChange={onChange}
            onSectionChange={onSectionChange}
            showErrorList={false}
            transformErrors={transformErrors}
            extraErrors={extraErrors}
          >
            <></>
          </MuiForm>
        </SubmissionContextProvider>
      ) : (
        <LoadingPreview />
      )}
    </>
  );
}

export default React.memo(PaymentForm);
